export type Position = "start" | "middle" | "end" | "whole_word";

export const POSITIONS: Position[] = ["start", "middle", "end", "whole_word"];

export const UNSCORED_TYPES = new Set(["record", "intro", "reader", "map", "trace"]);

export interface GradableItem {
  type: string;
  correct?: unknown;
  correctSet?: unknown;
  accepted_positions?: unknown;
  accepted_answer_sets?: unknown;
  unscored?: boolean;
}

export function sameSet<T>(a: T[], b: T[]): boolean {
  if (a.length !== b.length) return false;
  const left = [...a].map((x) => JSON.stringify(x)).sort();
  const right = [...b].map((x) => JSON.stringify(x)).sort();
  return left.every((v, i) => v === right[i]);
}

export function asStringArray(selected: unknown): string[] {
  if (Array.isArray(selected)) return selected.map(String);
  if (selected == null || selected === "") return [];
  return [String(selected)];
}

export function evaluateChoice(selected: string, correct: string): boolean {
  return selected === correct;
}

export function evaluatePositions(selected: Position[], accepted: Position[]): boolean {
  return sameSet(selected, accepted);
}

export function evaluateGraphemes(selected: string[], accepted: string[]): boolean {
  return sameSet(selected, accepted);
}

export function evaluateOrder(selected: string[], correct: string[]): boolean {
  return selected.length === correct.length && selected.every((v, i) => v === correct[i]);
}

export function evaluateSlots(
  selected: Record<string, string>,
  correct: Record<string, string>,
): boolean {
  const keys = Object.keys(correct);
  return keys.every((k) => selected[k] === correct[k]);
}

function answerSet(item: GradableItem): unknown {
  return item.correctSet ?? item.accepted_answer_sets ?? item.accepted_positions;
}

export function asSlotRecord(selected: unknown): Record<string, string> {
  if (selected && typeof selected === "object" && !Array.isArray(selected)) {
    return Object.fromEntries(
      Object.entries(selected as Record<string, unknown>).map(([k, v]) => [k, String(v)]),
    );
  }
  return {};
}

/** Machine grade. Unscored items (local voice compare) never count as a scored hit. */
export function gradeAnswer(item: GradableItem, selected: unknown): boolean | null {
  if (item.unscored || UNSCORED_TYPES.has(item.type)) return null;
  const set = answerSet(item);
  if (set != null && set !== "") {
    const accepted = asStringArray(set);
    const picked = asStringArray(selected);
    if (item.type === "position" || item.accepted_positions) {
      return evaluatePositions(picked as Position[], accepted as Position[]);
    }
    return evaluateGraphemes(picked, accepted);
  }
  if (item.correct && typeof item.correct === "object" && !Array.isArray(item.correct)) {
    return evaluateSlots(asSlotRecord(selected), item.correct as Record<string, string>);
  }
  if (Array.isArray(item.correct)) {
    return evaluateOrder(asStringArray(selected), item.correct.map(String));
  }
  if (typeof item.correct === "string") {
    const picked = Array.isArray(selected) ? String(selected[0] ?? "") : String(selected ?? "");
    return evaluateChoice(picked, item.correct);
  }
  return false;
}
